import { CollectionViewer } from '@angular/cdk/collections';
import {
  AgrDataSource,
  PaginatedDataSource,
  PaginationModel,
  SortableDataSource,
  Sorting,
} from '@next/core-lib/table-v2';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  map,
  shareReplay,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  OrderOptions,
  RequestsOverviewModel,
} from 'src/app/dashboard/components/overview/models/overview.model';
import { PageAndSize } from 'src/app/dashboard/components/overview/models/paginated-overview.model';

import * as MyAdvertisementsActions from '../../store/actions/my-advertisements.actions';
import * as MyAdvertisementsSelectors from '../../store/selectors/my-advertisements.selector';

/* eslint-disable brace-style */
export class ActiveRequestsOverviewDataSource
  extends AgrDataSource<RequestsOverviewModel>
  implements PaginatedDataSource<RequestsOverviewModel>, SortableDataSource<RequestsOverviewModel>
{
  public data$ = new BehaviorSubject<RequestsOverviewModel[]>([]);
  requests$ = this.store.select(MyAdvertisementsSelectors.selectActiveRequests);
  readonly _sortingSubject$ = new BehaviorSubject<Sorting>({});

  readonly order$: Observable<OrderOptions> = this._sortingSubject$.pipe(
    map((sorting) => {
      const sortColumn = Object.keys(sorting)[0];
      const sortDirection = sorting[sortColumn];

      return sortColumn && sortDirection
        ? { column: sortColumn, descending: sortDirection === 'desc' }
        : undefined;
    }),
  );

  private pageAndSizeSubject$ = new BehaviorSubject<PageAndSize>({
    pageIndex: 0,
    pageSize: 25,
  });
  private readonly destroyPaginatorSubject$ = new Subject<void>();
  private readonly destroySubject$ = new Subject<boolean>();

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    super();

    combineLatest([this.pageAndSizeSubject$])
      .pipe(
        debounceTime(100),
        tap(() => this.loadingSubject.next(true)),
        takeUntil(this.destroySubject$),
      )
      .subscribe(([pageAndSize]) => {
        this.store.dispatch(MyAdvertisementsActions.loadActiveRequests({ pageAndSize }));
      });

    this.actions$
      .pipe(
        ofType(
          MyAdvertisementsActions.acceptMatchSuccess,
          MyAdvertisementsActions.rejectMatchSuccess,
          MyAdvertisementsActions.deleteRequestSuccess,
        ),
        withLatestFrom(this.pageAndSizeSubject$),
        takeUntil(this.destroySubject$),
        tap(([_, pageAndSize]) => {
          this.pageAndSizeSubject$.next(pageAndSize);
        }),
      )
      .subscribe();
  }

  connect(_: CollectionViewer): Observable<RequestsOverviewModel[]> {
    combineLatest([this.requests$, this.order$])
      .pipe(
        debounceTime(100),
        tap(() => this.loadingSubject.next(true)),
        takeUntil(this.destroySubject$),
        shareReplay(1),
      )
      .subscribe(([requests, order]) => {
        let sortedRequests = requests;

        if (order && order.column) {
          sortedRequests = this.sortRequests(requests, order.column, order.descending);
        }

        this.loadingSubject.next(false);
        this.data$.next(sortedRequests);
      });
    return this.data$;
  }

  disconnect(collectionViewer: CollectionViewer): void {
    super.disconnect(collectionViewer);
    this.destroySubject$.next(true);
    this.destroySubject$.complete();
  }

  connectPaginator(): Observable<PaginationModel> {
    return this.pageAndSizeSubject$.pipe(
      takeUntil(this.destroyPaginatorSubject$),
      map(({ pageIndex, pageSize }) => ({
        pageSize,
        pageIndex,
        length: this.data$.value.length,
      })),
    );
  }

  disconnectPaginator(): void {
    if (!this.destroyPaginatorSubject$.isStopped) {
      this.destroyPaginatorSubject$.next();
      this.destroyPaginatorSubject$.complete();
    }
  }

  applySorting(sorting: Sorting): void {
    this._sortingSubject$.next(sorting);
  }

  applyPage(pageIndex: number, pageSize: number): void {
    this.pageAndSizeSubject$.next({ pageIndex, pageSize });
  }

  sortRequests(
    requests: RequestsOverviewModel[],
    clickedColumn: string,
    descending: boolean,
  ): RequestsOverviewModel[] {
    const sortedRequests = [...requests];
    sortedRequests.sort((a, b) => {
      const property = clickedColumn as keyof RequestsOverviewModel;
      const valueA = a[property];
      const valueB = b[property];

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return descending ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
      }
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return descending ? valueB - valueA : valueA - valueB;
      }
      if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
        return descending ? (valueA ? 1 : -1) : valueA ? -1 : 1;
      }
      return 0;
    });
    return sortedRequests;
  }
}
